import MainPageService from "./MainPageService";
import {MainPageTypes} from "../../types/main-page";
import $ from "jquery";
import gsap from "../../helpers/gsapInstance";
import AsyncHelper from "../../helpers/AsyncHelper";
import MainPageCommonService from "./MainPageCommonService";
import {MAX_MOBILE_WIDTH} from "../../constans";
import Slider from "../../components/Slider";

export default class MainPageCardsService extends MainPageCommonService {
    private readonly $holder: JQuery;

    /* Массив всех карточек со всей главной страницы */
    public $allCards: JQuery;
    public $mobileCardsSliders: JQuery;

    /* Индекс активной карты */
    public activeCardIndex: number = 0;
    public totalCards: number = 4;

    /* Отступ вниз у карточек */
    public CARDS_Y_OFFSET: number = 300;

    public CARD_TRANSLATE_Y_TO_BOTTOM: number = 20;
    public CARD_OFFSET_X_RIGHT: number = 10;
    public CARD_SUBTRACT_WIDTH: number = 20;

    private readonly selectors = {
        allCards: ".js-main-page-card",
        mobileCardsSlider: ".js-main-page-cards-section-slider-with-options"
    };

    constructor($holder: JQuery) {
        super();

        this.$holder = $holder;

        this.$allCards = this.$holder.find(this.selectors.allCards);
        this.$mobileCardsSliders = this.$holder.find(this.selectors.mobileCardsSlider);

        this.convertCardOptions = this.convertCardOptions.bind(this);
        this.setCardsOffset = this.setCardsOffset.bind(this);
        this.prepareNextStepCards = this.prepareNextStepCards.bind(this);
        this.preparePreviousStepCards = this.preparePreviousStepCards.bind(this);
        this.showNextCard = this.showNextCard.bind(this);
        this.hidePreviousCard = this.hidePreviousCard.bind(this);
        this.initMobileCardsSliders = this.initMobileCardsSliders.bind(this);
    }

    /**
     * Конвертация px в формате vw resize
     * @private
     */
    private convertCardOptions(): MainPageTypes.ConvertedCardOptions {
        const CARD_TRANSLATE_Y_TO_BOTTOM = MainPageService.getConvertedValue(this.CARD_TRANSLATE_Y_TO_BOTTOM);
        const CARD_OFFSET_X_RIGHT = MainPageService.getConvertedValue(this.CARD_OFFSET_X_RIGHT);
        const CARD_SUBTRACT_WIDTH = MainPageService.getConvertedValue(this.CARD_SUBTRACT_WIDTH);

        return {
            CARD_TRANSLATE_Y_TO_BOTTOM,
            CARD_OFFSET_X_RIGHT,
            CARD_SUBTRACT_WIDTH
        }
    }

    /**
     * Задать offset Y для карточек
     * @public
     * @param cards
     * cards - массив карточек
     */
    public setCardsOffset(cards: JQuery<HTMLElement> = this.$allCards): void {
        const translateY = MainPageService.getConvertedValue(this.CARDS_Y_OFFSET);

        const styleValue = `translateY(${translateY})`;

        cards.each((_, card) => {
            card.style.transform = styleValue;
        });
    }

    /**
     * Показываем следующую карточку (используется для 2 или 5 экрана, передаем cards в виде массива jquery HTMLElement's)
     * @public
     * @param cards
     * cards - массив карточек
     */
    public async showNextCard(cards: JQuery<HTMLElement>): Promise<void> {
        if (this.activeCardIndex < cards.length) {
            /* Получаем активную карту из переданных карточек и добавляем ей this.ACTIVE_CLASS */
            const activeCard = cards.get(this.activeCardIndex);
            activeCard.classList.add(this.ACTIVE_CLASS);

            /* Выбранные карточки с 0 до активной */
            const slicedCards = cards.slice(0, this.activeCardIndex);

            /* Подготовленные slicedCards (задана ширина, отступ справа и отступ снизу для вывода открытых карточек сзади) */
            const preparedCards = this.prepareNextStepCards(slicedCards, activeCard);

            gsap.to(activeCard, {
                translateY: 0,
                duration: 0.25,
            });

            preparedCards.forEach(item => {
                gsap.to(item.cardElement, {
                    translateY: -item.translateY,
                    right: item.rightOffset,
                    width: item.subtractedWidth,
                    duration: 0.25,
                });
            })

            this.activeCardIndex++;

            await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
        }
    }

    /**
     * Подготавливаем slicedCards, при движении скролла ВНИЗ,
     * разворачиваем текущие открытые карточки,
     * задаем ширину, отступ справа и отступ снизу для вывода открытых карточек сзади
     * @private
     * @param cards
     * @param activeCard
     * cards - массив карточек
     * activeCard - активная карточка
     */
    private prepareNextStepCards(cards: JQuery<HTMLElement>, activeCard: HTMLElement): MainPageTypes.CardOptions[] {
        const {CARD_TRANSLATE_Y_TO_BOTTOM, CARD_OFFSET_X_RIGHT, CARD_SUBTRACT_WIDTH} = this.convertCardOptions();

        const preparedCards: MainPageTypes.CardOptions[] = [];

        let cardTranslateY: number = CARD_TRANSLATE_Y_TO_BOTTOM;
        let cardOffsetRight: number = CARD_OFFSET_X_RIGHT;
        let cardSubtractWidth: number = CARD_SUBTRACT_WIDTH;

        $(cards.get().reverse()).each((index, card) => {
            // first, потому что мы сделали reverse
            const isFirstElement = index === cards.length;

            const options: MainPageTypes.CardOptions = {
                translateY: isFirstElement ? 0 : cardTranslateY,
                rightOffset: isFirstElement ? 0 : cardOffsetRight,
                subtractedWidth: isFirstElement ? 0 : activeCard.offsetWidth - cardSubtractWidth,
                cardElement: card,
            }
            preparedCards.push(options);

            cardTranslateY += CARD_TRANSLATE_Y_TO_BOTTOM;
            cardOffsetRight += CARD_OFFSET_X_RIGHT;
            cardSubtractWidth += CARD_SUBTRACT_WIDTH;
        });

        return preparedCards;
    }

    /**
     * Скрытие предыдущей карточки (используется для 2 или 5 экрана, передаем cards в виде массива jquery HTMLElement's)
     * @private
     */
    public async hidePreviousCard(cards: JQuery<HTMLElement>): Promise<void> {
        if (this.activeCardIndex > 0) {
            this.activeCardIndex--;

            const activeCard: HTMLElement = cards.get(this.activeCardIndex);
            activeCard.classList.remove(this.ACTIVE_CLASS);

            const slicedCards = cards.slice(0, this.activeCardIndex);

            const preparedCards = this.preparePreviousStepCards(slicedCards, activeCard);

            const translateY = MainPageService.getConvertedValue(this.CARDS_Y_OFFSET);
            gsap.to(activeCard, {
                translateY: translateY,
                duration: 0.25,
            });

            preparedCards.forEach(item => {
                gsap.to(item.cardElement, {
                    translateY: -item.translateY,
                    right: item.rightOffset,
                    width: item.subtractedWidth,
                    duration: 0.25,
                });
            })

            await AsyncHelper.delay(this.ANIMATION_DURATION_IN_SECONDS, true);
        }
    }

    /**
     * Подготавливаем slicedCards, при движении скролла ВВЕРХ
     * разворачиваем текущие открытые карточки,
     * задаем ширину, отступ справа и отступ снизу для вывода открытых карточек сзади
     * @private
     * @param cards
     * @param activeCard
     * cards - массив карточек
     * activeCard - активная карточка
     */
    public preparePreviousStepCards(cards: JQuery<HTMLElement>, activeCard: HTMLElement): MainPageTypes.CardOptions[] {
        const {CARD_TRANSLATE_Y_TO_BOTTOM, CARD_OFFSET_X_RIGHT, CARD_SUBTRACT_WIDTH} = this.convertCardOptions();

        const preparedCards: MainPageTypes.CardOptions[] = [];

        let cardTranslateY: number = 0;
        let cardOffsetRight: number = 0;
        let cardSubtractWidth: number = 0;

        $(cards.get().reverse()).each((_, card) => {
            const options: MainPageTypes.CardOptions = {
                translateY: cardTranslateY,
                rightOffset: cardOffsetRight,
                subtractedWidth: activeCard.offsetWidth - cardSubtractWidth,
                cardElement: card,
            }
            preparedCards.push(options);

            cardTranslateY += CARD_TRANSLATE_Y_TO_BOTTOM;
            cardOffsetRight += CARD_OFFSET_X_RIGHT;
            cardSubtractWidth += CARD_SUBTRACT_WIDTH;
        });

        return preparedCards;
    }

    /**
     * Инициализация слайдера для карточек на main-page с опциями
     * @public
     */
    public initMobileCardsSliders(): void {
        this.$mobileCardsSliders.each((_, element) => {
            const isMobileDevice = this.detectMobileDevice();
            const MOBILE_PER_VIEW: number = 1;
            const TABLET_PER_VIEW: number = 2;

            const swiperSetting = {
                slidesPerView: MOBILE_PER_VIEW
            }

            if (isMobileDevice) {
                if (window.innerWidth <= MAX_MOBILE_WIDTH) {
                    swiperSetting.slidesPerView = MOBILE_PER_VIEW;
                } else {
                    swiperSetting.slidesPerView = TABLET_PER_VIEW;
                }
            }

            new Slider($(element), swiperSetting);
        });
    }
}
